/**
 * AdminAwareIdLink - Display ID with optional admin link
 *
 * Shows a global ID. For admin users, renders it as a clickable link to the object inspector.
 * For non-admin users, renders it as plain text.
 */

import { useShouldShowAdmin } from '@/utils/admin';
import { objUrl } from '@/utils/urls';
import { Button } from '@/components/atoms/Button/Button';

interface AdminAwareIdLinkProps {
  /** Global ID to display */
  id: string;

  /** Optional label prefix (default: "ID: ") */
  label?: string;

  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * Display a global ID with optional admin link functionality
 *
 * @example
 * <AdminAwareIdLink id="ph1a2b" />
 *
 * @example
 * <AdminAwareIdLink id="ph1a2b" label="Photo: " className="text-sm" />
 */
export function AdminAwareIdLink({
  id,
  label = 'ID: ',
  className = ''
}: AdminAwareIdLinkProps) {
  const shouldShowAdmin = useShouldShowAdmin();

  const handleIdClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(objUrl(id), '_blank');
  };

  if (shouldShowAdmin) {
    return (
      <span className={className}>
        {label}
        <Button
          variant="link"
          size="xs"
          onClick={handleIdClick}
          className="h-auto p-0 text-blue-400 hover:text-blue-300"
        >
          {id}
        </Button>
      </span>
    );
  }

  return (
    <span className={className}>
      {label}{id}
    </span>
  );
}
